import { forkJoin, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';

import { PaymentMethodsApiService } from '@app/settings/shared/modules/payment-methods/services/payment-methods-api.service';
import { defaultToastError } from '@app/shared/operators/default-toast-error.operator';
import {
  DeletePaymentMethod,
  GetPaymentMethods,
  GetPaymentMethodSecret,
  SetPaymentMethodAsDefault,
} from './payment-methods-state.actions';
import {
  CUSTOMER_PAYMENT_METHODS_STATE_TOKEN,
  CustomerPaymentMethodsStateModel,
  PaymentMethod,
  PaymentMethodSetup,
} from './payment-methods-state.models';

type ThisStateCtx = StateContext<CustomerPaymentMethodsStateModel>;

@Injectable()
@State<CustomerPaymentMethodsStateModel>({
  name: CUSTOMER_PAYMENT_METHODS_STATE_TOKEN,
  defaults: { methods: [] },
})
export class PaymentMethodsState {
  @Selector()
  static setup({ setup }: CustomerPaymentMethodsStateModel): PaymentMethodSetup | undefined {
    return setup;
  }

  @Selector()
  static methods({ methods }: CustomerPaymentMethodsStateModel): PaymentMethod[] {
    return methods || [];
  }

  @Selector([PaymentMethodsState.methods])
  static hasPaymentMethods(methods: PaymentMethod[]): boolean {
    return !!methods.length;
  }

  constructor(private pm: PaymentMethodsApiService) {}

  @Action(GetPaymentMethods)
  getPaymentMethods({ setState }: ThisStateCtx): Observable<unknown> {
    return this.pm.getPaymentMethods().pipe(map((methods) => setState(patch({ methods }))));
  }

  @Action(GetPaymentMethodSecret)
  getPaymentMethodSecret({ setState }: ThisStateCtx): Observable<unknown> {
    return this.pm.getPaymentMethodSetup().pipe(tap((setup: PaymentMethodSetup) => setState(patch({ setup }))));
  }

  @Action(SetPaymentMethodAsDefault)
  setPaymentMethodAsDefault(
    { dispatch }: ThisStateCtx,
    { paymentMethodId }: SetPaymentMethodAsDefault,
  ): Observable<unknown> {
    return this.pm.setAsDefault(paymentMethodId).pipe(
      defaultToastError(),
      switchMap(() => dispatch(new GetPaymentMethods())),
    );
  }

  @Action(DeletePaymentMethod)
  deletePaymentMethod(
    { dispatch, getState }: ThisStateCtx,
    { paymentMethodId }: DeletePaymentMethod,
  ): Observable<unknown> {
    const { methods } = getState();
    const otherMethod = methods.find((method) => method.paymentMethodId !== paymentMethodId);

    return forkJoin([
      this.pm.removeMethod(paymentMethodId),
      ...(otherMethod && !otherMethod.default
        ? [dispatch(new SetPaymentMethodAsDefault(otherMethod.paymentMethodId))]
        : []),
    ]).pipe(
      defaultToastError(),
      switchMap(() => dispatch(new GetPaymentMethods())),
    );
  }
}
